import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import BlogCreditScoreImage from '../../../assets/blog-credit-score.png';
import BlogSixTipsImage from '../../../assets/blog-six-tips.png';
import ApplicationIconSVG from '../../../assets/icons/application-icon.svg';
import ChevronSVG from '../../../assets/icons/chevron.svg';
import CheckMarkSVG from '../../../assets/icons/round-orange-checkmark.svg';
import { CreditCardApplicationContext } from '../../../CreditCardApplicationContext';
import { ActionAnchor } from 'src/commons/components/Button';
import Card, {
  ErrorCard,
  LoadingCard,
  RetryWorkflowEventCard,
} from 'src/commons/components/Card';
import FileDownloadLink from 'src/commons/components/FileDownloadLink';
import Heading from 'src/commons/components/Heading';
import SubHeading from 'src/commons/components/SubHeading';
import {
  MOBILE_BREAKPOINT,
  CARD_BREAKPOINT,
  colors,
  ApplicationWorkflowEvent,
  ApplicationStatus,
  PageNames,
  AMONE_DECLINE_BASE_LINK,
  AMONE_DECLINE_URL,
  GraphQLErrorCodes,
  UNAUTHORIZED_MESSAGE,
} from 'src/commons/constants';
import { processCreditCardApplicationWorkflowEvent } from 'src/commons/data/queries';
import {
  logError,
  nrTrackCreditCardApplicationWorkflowEvent,
} from 'src/commons/utilities/logging';
import { prefixRoute } from 'src/commons/utilities/routes';
import {
  useApplicationPolling,
  PollingStatus,
} from 'src/commons/utilities/useApplicationPolling';

// Decline
// Poll the application while the status is REJECTION_IN_PROGRESS
// Show a LoadingBar while polling app once per second with a timeout of 60 seconds
// Show main decline page content when APPLICATION_REJECTED is polled
// Display RetryWorkflowEventCard when REJECTION_ERROR is polled
// Display the ErrorCard when timeout occurs

// Possible statuses at conclusion of app polling:
//  - APPLICATION_REJECTED  : Show main decline page content with link to Agreements page
//  - REJECTION_ERROR       : Show RetryWorkflowEventCard and allow REJECTION_RETRIED
//  - REJECTION_IN_PROGRESS : Show ErrorCard

// Interface --------------------------------------------------------------------------------------
interface GenericDeclineProps {
  applicationId: string;
}

interface AmOneInvitationProps {
  amOneLink: string;
}

// Constants --------------------------------------------------------------------------------------
const POLLING_TIMEOUT = 60000; // maximum time in ms to poll the application

// Styles -----------------------------------------------------------------------------------------
// #region Styled components
const StyledCard = styled(Card)`
  min-height: 19.875rem;
  @media (max-width: ${CARD_BREAKPOINT}) {
    margin: 0;
    padding: 0 1rem;
  }
`;
StyledCard.displayName = 'StyledCard';

const DeclineContentCard = styled(StyledCard)`
  margin-top: 1rem;
`;
DeclineContentCard.displayName = 'DeclineContentCard';

const DeclineAgreementsCard = styled(StyledCard)``;
DeclineAgreementsCard.displayName = 'DeclineAgreementsCard';

const AmOneCard = styled(DeclineContentCard)``;
AmOneCard.displayName = 'AmOneCard';

const BlogCard = styled(DeclineContentCard)``;
BlogCard.displayName = 'BlogCard';

// #region DeclineAgreementsCard styles
const ApplicationIcon = styled(ApplicationIconSVG)`
  width: 2.75rem;
  height: auto;
  margin: 2.875rem 0 0;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 2.8125rem 0 0;
  }
`;
ApplicationIcon.displayName = 'DeclineApplicationIcon';

const DeclineHeading = styled(Heading)`
  margin: 1.5rem 0 0;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 18rem;
    margin: 1.625rem 0 0;
  }
`;
DeclineHeading.displayName = 'DeclineHeading';

const DeclineContentText = styled(SubHeading)`
  max-width: 29.6875rem;
  margin: 1.5625rem 0 3rem;
  font-size: 1rem;
  line-height: 1.5rem;
  text-align: center;
  color: ${colors.TEXT_PRIMARY};
  @media (max-width: ${CARD_BREAKPOINT}) {
    max-width: 25rem;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: unset;
    margin: 1.8125rem 0.5rem 3rem;
  }
`;
DeclineContentText.displayName = 'DeclineContentText';

const LegalAgreementsAndDisclosuresLink = styled(FileDownloadLink)``;
LegalAgreementsAndDisclosuresLink.displayName =
  'LegalAgreementsAndDisclosuresLink';
// #endregion

// #region AmOneCard styles
const AmOneCardHeading = styled(DeclineHeading)`
  max-width: 25.3125rem;
  margin: 3.25rem 0 0;
  line-height: 2rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 19.1875rem;
    margin: 3.25rem 0 0;
  }
  b {
    font-weight: 600;
    color: ${colors.UI_PRIMARY_ACTION};
  }
`;
AmOneCardHeading.displayName = 'AmOneCardHeading';

const AmOneLogo = styled.img`
  height: 1.5rem;
  width: 6.6875rem;
  margin: 3.375rem 0 0;
`;
AmOneLogo.displayName = 'AmOneLogo';

const AmOneFeatureList = styled.div`
  margin: 1.6875rem 3.0625rem 0;
`;
AmOneFeatureList.displayName = 'AmOneFeatureList';

const AmOneFeatureListItem = styled.div`
  min-width: 16.125rem;
  display: flex;
  align-items: flex-start;
  margin: 0 0 0.25rem;
  :last-child {
    margin: 0;
  }
`;
AmOneFeatureListItem.displayName = 'AmOneFeatureListItem';

const AmOneFeatureListItemBullet = styled.div`
  max-height: 0.9375rem;
  margin: 0.3125rem 0.625rem 0 0;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  svg {
    min-height: 0.9375rem;
    min-width: 0.9375rem;
  }
`;
AmOneFeatureListItemBullet.displayName = 'AmOneFeatureListItemBullet';

const AmOneFeatureListItemText = styled.p`
  color: ${colors.TEXT_PRIMARY};
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5rem;
  height: auto;
`;
AmOneFeatureListItemText.displayName = 'AmOneFeatureListItemText';

const AmoneDisclaimer = styled.p`
  color: ${colors.TEXT_PRIMARY};
  margin-top: 1.25rem;
  font-size: 0.875rem;
  line-height: 2rem;
  margin-bottom: 3.375rem;
`;
AmoneDisclaimer.displayName = 'AmoneDisclaimer';

const AmOneNewRedirectLink = styled(ActionAnchor)`
  margin: 2.25rem 0 0rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 3.75rem 0 0rem;
  }
`;
AmOneNewRedirectLink.displayName = 'AmOneNewRedirectLink';
// #endregion

// #region BlogCard styles
const BlogCardHeading = styled(DeclineHeading)`
  max-width: 27rem;
  margin: 3.4375rem 0 0;
  line-height: 2rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 20rem;
  }
`;
BlogCardHeading.displayName = 'BlogCardHeading';

const ProsperBlogLink = styled.a`
  margin: 1.625rem 0 2.4375rem;
  font-size: 1rem;
  text-decoration: none;
  color: ${colors.TEXT_ACTION};
`;
ProsperBlogLink.displayName = 'DeclineProsperBlogLink';

const ProsperBlogLinkChevron = styled(ChevronSVG)`
  width: 0.5rem;
  margin: 0 0 0.0625rem 0.25rem;
  path {
    stroke: ${colors.TEXT_ACTION};
    stroke-width: 2.5;
  }
  transform: rotate(270deg);
`;
ProsperBlogLinkChevron.displayName = 'DeclineProsperBlogLinkChevron';

const BlogImageContainer = styled.div`
  width: 100%;
  margin: 0 0 2.4375rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 0 2.25rem;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
  }
`;
BlogImageContainer.displayName = 'DeclineBlogImageContainer';

const BlogImageLink = styled.a`
  position: relative;
  flex: 1;
  :first-of-type {
    margin: 0 0.625rem 0 0;
  }
  :last-of-type {
    margin: 0 0 0 0.625rem;
  }
  @media (max-width: ${CARD_BREAKPOINT}) {
    :first-of-type {
      margin: 0 0.5rem 0 0;
    }
    :last-of-type {
      margin: 0 0 0 0.5rem;
    }
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    :first-of-type,
    :last-of-type {
      margin: 0 0 1rem;
    }
  }
`;
BlogImageLink.displayName = 'DeclineBlogImageLink';

const BlogImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 0.5rem;
  object-fit: contain;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    object-fit: cover;
    max-height: 16.1875rem;
  }
`;
BlogImage.displayName = 'DeclineBlogImage';

const BlogTitle = styled.p`
  position: absolute;
  padding: 0 1rem 3rem;
  left: 0;
  bottom: 0;
  font-weight: 700;
  font-size: 1.0625rem;
  line-height: 1.1875;
  color: #ffffff;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 1rem 3.625rem;
    font-size: 1.25rem;
    line-height: 1.125;
  }
`;
BlogTitle.displayName = 'BlogTitle';

const BlogTime = styled.p`
  position: absolute;
  padding: 0 1rem 1.6875rem;
  left: 0;
  bottom: 0;
  font-size: 0.75rem;
  color: #ffffff;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 1rem 2rem;
    font-size: 0.875rem;
  }
`;
BlogTime.displayName = 'BlogTime';
// #endregion
// #endregion

// Components -------------------------------------------------------------------------------------
// #region Decline Subcomponents
// Decline page content is shown for application status APPLICATION_REJECTED
// component for showing generic declines when there's no specified or recognized decline code
const GenericDecline = (props: GenericDeclineProps) => {
  const { applicationId } = props;
  return (
    <DeclineAgreementsCard>
      <ApplicationIcon />
      <DeclineHeading>Thank you for your application</DeclineHeading>
      <DeclineContentText>
        After reviewing your information, we cannot approve you for a credit
        card. The good news? Your credit score has not been affected. For more
        about this decision, see your Adverse Action Notice on your&nbsp;
        <LegalAgreementsAndDisclosuresLink
          id="decline-link-legal-agreements"
          href={prefixRoute(`/application/${applicationId}/agreements`)}
        >
          legal agreements and disclosures
        </LegalAgreementsAndDisclosuresLink>
        &nbsp;page.
      </DeclineContentText>
    </DeclineAgreementsCard>
  );
};

// AmOne Decline content is shown below the GenericDecline content for all declined users
const AmOneInvitationNew = (props: AmOneInvitationProps) => {
  const { amOneLink } = props;
  return (
    <AmOneCard>
      <AmOneCardHeading>
        A personal loan through AmONE could be a good alternative
      </AmOneCardHeading>
      <AmOneFeatureList>
        <AmOneFeatureListItem>
          <AmOneFeatureListItemBullet>
            <CheckMarkSVG />
          </AmOneFeatureListItemBullet>
          <AmOneFeatureListItemText>
            Have cash on hand for big purchases or emergencies
          </AmOneFeatureListItemText>
        </AmOneFeatureListItem>
        <AmOneFeatureListItem>
          <AmOneFeatureListItemBullet>
            <CheckMarkSVG />
          </AmOneFeatureListItemBullet>
          <AmOneFeatureListItemText>
            Credit score may improve through on-time payments
          </AmOneFeatureListItemText>
        </AmOneFeatureListItem>
        <AmOneFeatureListItem>
          <AmOneFeatureListItemBullet>
            <CheckMarkSVG />
          </AmOneFeatureListItemBullet>
          <AmOneFeatureListItemText>
            Offers for a range of credit scores & financial situations
          </AmOneFeatureListItemText>
        </AmOneFeatureListItem>
      </AmOneFeatureList>
      <AmOneNewRedirectLink id="decline-link-amone-invitation" href={amOneLink}>
        Explore personal loan options
      </AmOneNewRedirectLink>
      <AmoneDisclaimer>
        AmONE matches consumers to trusted lenders
      </AmoneDisclaimer>
    </AmOneCard>
  );
};

// Card with Prosper blog links
const DeclineBlogs = () => (
  <BlogCard>
    <BlogCardHeading>
      While you’re focusing on financial wellness, consider taking advantage of
      our free resource center
    </BlogCardHeading>
    <ProsperBlogLink
      id="decline-prosper-blog-link"
      href="https://www.prosper.com/blog"
      target="_blank"
      rel="noreferrer noopener"
    >
      Prosper Blog
      <ProsperBlogLinkChevron />
    </ProsperBlogLink>
    <BlogImageContainer>
      <BlogImageLink
        href="https://www.prosper.com/blog/5-tips-to-boost-credit-score/"
        target="_blank"
        rel="noreferrer noopener"
        id="decline-tips-boost-credit-score-blog-link"
        aria-label="Boost Your Credit Score: 5 Tips"
      >
        <BlogImage
          src={BlogSixTipsImage}
          alt="5-tips-improve-credit-score-blog-image"
        />
        <BlogTitle>
          Boost Your Credit
          <br />
          Score: 5 Tips
        </BlogTitle>
        <BlogTime>4 min read</BlogTime>
      </BlogImageLink>
      <BlogImageLink
        href="https://www.prosper.com/blog/what-is-a-good-credit-score/"
        target="_blank"
        rel="noreferrer noopener"
        id="decline-credit-score-blog-link"
        aria-label="What is a Good Credit Score?"
      >
        <BlogImage
          src={BlogCreditScoreImage}
          alt="what-is-a-good-credit-score-blog-image"
        />
        <BlogTitle>
          What is a good credit
          <br />
          score?
        </BlogTitle>
        <BlogTime>3 min read</BlogTime>
      </BlogImageLink>
    </BlogImageContainer>
  </BlogCard>
);
// #endregion

// Main Decline component
// Renders the page and one of a few decline components depending on decline code
export const Decline = () => {
  // Context --------------------------------------------------------------------------------------
  const {
    creditCardApplication,
    fetchCreditCardApplication,
    fetchAndResumeCreditCardApplication,
    fetchingApplication,
    showApplicationFetchError,
  } = useContext(CreditCardApplicationContext);

  // State ----------------------------------------------------------------------------------------
  const [polling, setPolling] = useState(true); // true while polling application
  const [success, setSuccess] = useState(false); // true when APPLICATION_REJECTED
  const [retry, setRetry] = useState(false); // show Retry component if REJECTION_ERROR
  const [error, setError] = useState(false); // show error if networking failed or on timeout
  const [timedOut, setTimedOut] = useState(false); // reset timeout if REJECTION_RETRIED
  const [offerCheck, setOfferCheck] = useState(null); // determine decline type for AmOne URL
  const [amOneLink, setAmOneLink] = useState(AMONE_DECLINE_BASE_LINK); // URL for AmOne redirect

  // Hooks ----------------------------------------------------------------------------------------
  // Grab the application id and decline code from the route params
  const { applicationId, declineCode } = useParams();
  const [appPollState] = useApplicationPolling(applicationId, polling);

  // Pass to RetryWorkflowEventCard to allow REJECTION_RETRIED when the status is REJECTION_ERROR
  const retryRejection = async () => {
    try {
      const rejectionRetriedEvent = ApplicationWorkflowEvent.REJECTION_RETRIED;
      const response = await processCreditCardApplicationWorkflowEvent(
        applicationId,
        rejectionRetriedEvent
      );
      const { data, errors } = response;
      nrTrackCreditCardApplicationWorkflowEvent(data);
      if (data && data.processCreditCardApplicationWorkflowEvent) {
        // Reset timeout and restart polling if workflow event was successful
        setPolling(true);
      } else if (errors && errors.length > 0 && errors[0].code) {
        switch (errors[0].code) {
          case GraphQLErrorCodes.TERMINAL_APPLICATION:
            setSuccess(true);
            setRetry(false);
            break;
          case GraphQLErrorCodes.INVALID_WORKFLOW_EVENT_FOR_STATUS:
            if (/REJECTION_IN_PROGRESS/.test(errors[0].message)) {
              setPolling(true);
            } else {
              // Redirect to the correct page according to the application status
              fetchAndResumeCreditCardApplication(PageNames.DECLINE);
            }
            break;
          default:
            setError(true);
        }
      } else {
        setError(true);
      }
    } catch (err: any) {
      setError(true);
      if (err.message !== UNAUTHORIZED_MESSAGE) {
        logError(err, {
          errorName: err.name || 'PageError',
          errorMessage: err.message || 'Unexpected error occurred',
          component: 'Decline',
          function: 'retryRejection',
        });
      }
    }
  };

  // Determines which decline content to display based on the decline code
  const getDeclineContent = () => {
    switch (declineCode) {
      default:
        return <GenericDecline applicationId={applicationId} />;
    }
  };

  // Redirect if necessary if status is not within the rejection flow
  useEffect(() => {
    fetchAndResumeCreditCardApplication(PageNames.DECLINE);
  }, []);

  // Poll application - timeout after max time elapses and show ErrorCard
  // Reset timeout if REJECTION_ERROR occurs and allow retry via RetryWorkflowEventCard
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!timedOut && polling && !success) {
      // Start/restart timeout
      timer = setTimeout(() => {
        setTimedOut(true);
      }, POLLING_TIMEOUT);
    } else if (timedOut) {
      setPolling(false);
    }
    return () => clearTimeout(timer);
  }, [timedOut]);

  useEffect(() => {
    if (polling && retry) {
      setRetry(false);
      setTimedOut(false);
    } else if (!polling && !retry && !success && timedOut) {
      setError(true);
    }
  }, [polling]);

  // Continue polling application until status transitions from REJECTION_IN_PROGRESS
  useEffect(() => {
    // Check application status while polling is underway
    if (appPollState.pollingStatus === 'ACTIVE') {
      const { application } = appPollState;
      const { applicationStatus, offerCheck: polledOfferCheck } = application;
      switch (applicationStatus) {
        case ApplicationStatus.APPLICATION_REJECTED:
          // Rejection Success - Stop polling when the rejection process is complete
          setSuccess(true);
          setPolling(false);
          // Cache offerCheck to construct the AmOneInvitation link
          setOfferCheck(polledOfferCheck);
          break;
        case ApplicationStatus.REJECTION_ERROR:
          // Rejection Failure - Stop polling and show RetryWorkflowEventCard
          setRetry(true);
          setPolling(false);
          break;
        case ApplicationStatus.REJECTION_IN_PROGRESS:
          // In Progress - Continue polling until application status changes
          break;
        default:
          // Unexpected Status - Stop polling and redirect accordingly
          setPolling(false);
          break;
      }
    } else if (appPollState.pollingStatus === PollingStatus.FAILURE) {
      // Stop polling if an error occurred fetching the application
      setError(true);
      setPolling(false);
    }
  }, [appPollState]);

  // Ensure the application offerCheck is available to construct the AmOne redirect link
  useEffect(() => {
    if (!success) return;
    if (offerCheck) {
      // Construct the AmOne link using offerCheck obtained by polling or refreshing context
      const amOneRedirectLink = AMONE_DECLINE_URL(offerCheck);
      setAmOneLink(amOneRedirectLink);
    } else if (
      creditCardApplication &&
      creditCardApplication.applicationStatus &&
      creditCardApplication.applicationStatus ===
        ApplicationStatus.APPLICATION_REJECTED
    ) {
      // Ensure context application has status APPLICATION_REJECTED before caching its offerCheck
      const { offer } = creditCardApplication.offerCheck;
      // Check for null offer explicitly in case the user was declined at the offer stage
      if (offer || offer === null)
        setOfferCheck(creditCardApplication.offerCheck);
      // Show error if the offerCheck has an unexpected value when APPLICATION_REJECTED
      else setError(true);
    } else {
      // Refresh stale context after successfully processing REJECTION_RETRIED
      fetchCreditCardApplication();
    }
  }, [success, offerCheck]);

  if (fetchingApplication || polling) {
    return <LoadingCard />;
  }

  if (retry) {
    return <RetryWorkflowEventCard onRetry={retryRejection} />;
  }

  if (success) {
    return (
      <>
        {getDeclineContent()}
        <AmOneInvitationNew amOneLink={amOneLink} />
        <DeclineBlogs />
      </>
    );
  }

  if (showApplicationFetchError || error || timedOut) {
    return <ErrorCard />;
  }

  // Fallback to ErrorCard
  return <ErrorCard />;
};
